import type { IGetExamInfo } from '@/api/start';

export const EXAM_SUBMIT_TIMEOUT_MS = 20_000;
export const SUFE_SUBMIT_RETRY_MS = 3_000;
export const SUFE_SUBMIT_MAX_RETRY_MS = 30_000;

export type MockExamCourse = 'listening' | 'reading' | 'writing';
export type ExamSubmissionState = 'editable' | 'processing' | 'submitted';

export function getExamSubmissionState(info: IGetExamInfo, recordId: number, course: MockExamCourse): ExamSubmissionState {
  if (info?.record_id !== recordId || !info[course]) {
    throw new Error('exam state does not match the active record');
  }
  const {status, submission_pending: pending} = info[course];
  if (pending === true) return 'processing';
  if (status === 4 || (course === 'writing' && status === 3 && pending === false)) return 'submitted';
  // Older servers cannot distinguish a temporary writing reservation from a
  // committed submission. Keep reconciling until a definitive result is available.
  if (status === 3) return 'processing';
  if (status === 1 || status === 2) return 'editable';
  throw new Error('unknown exam submission state');
}

export async function settleExamRequest<T>(
  work: (signal: AbortSignal) => Promise<T>,
  timeoutMs = EXAM_SUBMIT_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<T> {
  if (signal?.aborted) throw new Error('exam page disposed');
  const request = new AbortController();
  let interrupt!: (error: Error) => void;
  const interrupted = new Promise<never>((_, reject) => { interrupt = reject; });
  const abort = () => {
    request.abort();
    interrupt(new Error('exam page disposed'));
  };
  signal?.addEventListener('abort', abort, {once: true});
  const timer = setTimeout(() => {
    request.abort();
    interrupt(new Error('exam request timed out'));
  }, timeoutMs);
  try {
    return await Promise.race([work(request.signal), interrupted]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

export async function finishExamSubmission(options: {
  readState: (signal: AbortSignal) => Promise<ExamSubmissionState>;
  // Omit only when restoring a server-owned draft after a pending submission.
  save?: (signal: AbortSignal) => Promise<unknown>;
  submit: (signal: AbortSignal) => Promise<unknown>;
  timeoutMs?: number;
  signal?: AbortSignal;
}): Promise<boolean> {
  const run = <T>(work: (signal: AbortSignal) => Promise<T>) => settleExamRequest(work, options.timeoutMs, options.signal);
  let attemptedWrite = false;
  try {
    const state = await run(options.readState);
    if (state === 'submitted') return true;
    attemptedWrite = true;
    if (state === 'editable' && options.save) {
      const saved = await run(options.save);
      if (!saved || typeof saved !== 'object' || !('id' in saved) || typeof saved.id !== 'number' || saved.id <= 0) {
        return false;
      }
    }
    // A writing reservation can outlive a crashed server process. Retrying the
    // submit lets the server reclaim it once stale; never overwrite its answers.
    const submitted = await run(options.submit);
    if (submitted == null || submitted === false) {
      return await run(options.readState) === 'submitted';
    }
    return submitted != null && submitted !== false;
  } catch {
    if (!attemptedWrite || options.signal?.aborted) return false;
    // A rejected or timed-out response says nothing about whether the server
    // committed. Reconcile before allowing the caller to retry the write.
    try {
      return await run(options.readState) === 'submitted';
    } catch {
      return false;
    }
  }
}
